import { Component } from "@theme/component";
import { ThemeEvents, MediaStartedPlayingEvent } from "@theme/events";
import { DialogCloseEvent } from "@theme/dialog";
import { prefersReducedMotion } from "@theme/utilities";

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #ctaScrollAutoplayObserver;

  /** @type {HTMLVideoElement | undefined} */
  #ctaViewportMedia;

  /** Last in-view state from the CTA viewport observer (for visibility resume). */
  #ctaInView = false;

  /** Toggle button: prefer ref, else known class (refs are not always wired). */
  #getToggleMediaButton() {
    const fromRef = this.refs.toggleMediaButton;
    if (fromRef instanceof HTMLElement) return fromRef;
    return this.querySelector("button.video-interaction-hint");
  }

  /** Inlined autoplay media is a direct child (after poster + `<template>`). */
  #findInlinedMediaRoot() {
    for (const child of this.children) {
      if (child instanceof HTMLVideoElement) return child;
      if (child instanceof HTMLIFrameElement) {
        if (child.dataset.videoType || child.classList.contains("js-youtube") || child.classList.contains("js-vimeo")) {
          return child;
        }
      }
    }
    return null;
  }

  /** @returns {HTMLIFrameElement | null} */
  #findCommandIframe() {
    return (
      this.querySelector("iframe[data-video-type]") ||
      this.querySelector("iframe.js-youtube") ||
      this.querySelector("iframe.js-vimeo")
    );
  }

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    this.#syncCtaReducedMotionNativePause();

    queueMicrotask(() => {
      if (this.#setupCtaNativeVideoViewportAutoplay()) return;
      this.#syncAutoplayPlayPauseUi();
    });
  }

  /** Pause native CTA video before first paint when the user prefers reduced motion (no viewport autoplay). */
  #syncCtaReducedMotionNativePause() {
    if (!prefersReducedMotion()) return;
    if (!this.classList.contains("cta-module__video-root")) return;
    if (!this.hasAttribute("autoplay")) return;

    const media = this.#findInlinedMediaRoot();
    if (!(media instanceof HTMLVideoElement)) return;

    media.pause();
    media.removeAttribute("autoplay");
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
  }

  #onDocumentVisibilityChange = () => {
    if (!this.#ctaScrollAutoplayObserver || !(this.#ctaViewportMedia instanceof HTMLVideoElement)) return;

    const media = this.#ctaViewportMedia;
    if (document.visibilityState === "hidden") {
      media.pause();
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
      return;
    }

    if (this.#ctaInView && !prefersReducedMotion()) {
      media.setAttribute("autoplay", "");
      media.play().catch(() => {});
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
    }
  };

  #onReducedMotionMediaChange = () => {
    if (!this.#ctaScrollAutoplayObserver || !(this.#ctaViewportMedia instanceof HTMLVideoElement)) return;

    const media = this.#ctaViewportMedia;
    if (prefersReducedMotion()) {
      media.pause();
      media.removeAttribute("autoplay");
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
      return;
    }

    if (this.#ctaInView && document.visibilityState === "visible") {
      media.setAttribute("autoplay", "");
      media.play().catch(() => {});
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
    }
  };

  /**
   * CTA uploaded video (`cta-module__video-root` + native `<video>`): play while the block is in view,
   * pause when it leaves. First `.shopify-section` may start from native autoplay before the first
   * callback; later sections start only after entering view. With `prefers-reduced-motion: reduce`,
   * no viewport autoplay (user can still use the corner control). Pauses when the tab is hidden and
   * resumes when visible if still in view. YouTube/Vimeo URL embeds unchanged.
   * @returns {boolean} True when this path owns playback (skip `#syncAutoplayPlayPauseUi`).
   */
  #setupCtaNativeVideoViewportAutoplay() {
    if (!this.hasAttribute("autoplay")) return false;
    if (!this.classList.contains("cta-module__video-root")) return false;

    const media = this.#findInlinedMediaRoot();
    if (!(media instanceof HTMLVideoElement)) return false;

    this.#ctaViewportMedia = media;

    const sections = document.querySelectorAll(".shopify-section");
    const firstSection = sections[0];
    const mySection = this.closest(".shopify-section");
    const isFirstSection = Boolean(firstSection && mySection && mySection === firstSection);

    if (!isFirstSection) {
      media.pause();
      media.removeAttribute("autoplay");
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
    }

    const signal = this.#abortController.signal;
    document.addEventListener("visibilitychange", this.#onDocumentVisibilityChange, {
      signal
    });
    matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", this.#onReducedMotionMediaChange, {
      signal
    });

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#ctaInView = entry.isIntersecting;

          if (entry.isIntersecting) {
            if (!this.getAttribute("data-media-loaded")) {
              this.setAttribute("data-media-loaded", "true");
            }

            if (prefersReducedMotion()) {
              continue;
            }

            media.setAttribute("autoplay", "");
            if (document.visibilityState === "visible") {
              media.play().catch(() => {});
              this.isPlaying = true;
              this.updatePlayPauseHint(true);
            } else {
              this.isPlaying = false;
              this.updatePlayPauseHint(false);
            }
          } else {
            media.pause();
            media.removeAttribute("autoplay");
            this.isPlaying = false;
            this.updatePlayPauseHint(false);
          }
        }
      },
      { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0.2 }
    );
    io.observe(this);
    this.#ctaScrollAutoplayObserver = io;
    return true;
  }

  /**
   * Autoplay inlines `<video>` / `<iframe>` so `showDeferredMedia` never runs. One place to set
   * `data-media-loaded`, `isPlaying`, and play/pause icons (Liquid may already show the corner control).
   */
  #syncAutoplayPlayPauseUi() {
    if (!this.hasAttribute("autoplay")) return;

    const run = () => {
      const media = this.#findInlinedMediaRoot();
      const toggle = this.#getToggleMediaButton();
      if (!media || !toggle) return false;
      if (!this.getAttribute("data-media-loaded")) {
        this.setAttribute("data-media-loaded", "true");
      }
      this.refs.deferredMediaPlayButton?.classList.add("deferred-media__playing");
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      return true;
    };

    if (run()) return;
    requestAnimationFrame(() => {
      if (run()) return;
      setTimeout(() => run(), 250);
    });
  }

  disconnectedCallback() {
    this.#ctaScrollAutoplayObserver?.disconnect();
    this.#ctaScrollAutoplayObserver = undefined;
    this.#ctaViewportMedia = undefined;
    this.#ctaInView = false;
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.#getToggleMediaButton();
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove("hidden");
      toggleMediaButton.setAttribute("aria-pressed", isPlaying ? "true" : "false");

      const labelPlay = toggleMediaButton.dataset.labelPlay;
      const labelPause = toggleMediaButton.dataset.labelPause;
      if (labelPlay && labelPause) {
        toggleMediaButton.setAttribute("aria-label", isPlaying ? labelPause : labelPlay);
      }

      const playIcon = toggleMediaButton.querySelector(".icon-play");
      if (playIcon) playIcon.classList.toggle("hidden", isPlaying);
      const pauseIcon = toggleMediaButton.querySelector(".icon-pause");
      if (pauseIcon) pauseIcon.classList.toggle("hidden", !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute("data-media-loaded")) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector("template")?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute("data-media-loaded", "true");
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add("deferred-media__playing");

    if (content instanceof HTMLVideoElement && content.getAttribute("autoplay")) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.#findCommandIframe();
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        "*"
      );
    } else {
      this.querySelector("video")?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.#findCommandIframe();

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"' + "pauseVideo" + '","args":""}'
          : '{"method":"pause"}',
        "*"
      );
    } else {
      this.querySelector("video")?.pause();
    }
    this.isPlaying = false;

    const toggle = this.#getToggleMediaButton();
    const toggleVisible = toggle instanceof HTMLElement && !toggle.classList.contains("hidden");
    if (this.getAttribute("data-media-loaded") || toggleVisible) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this)
      }
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector("model-viewer");
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      "pointerdown",
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      "click",
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}
